/**
 * ProductsPage Component
 * Displays all products with search, filtering and sorting
 * Uses infinite scroll to load more products as user scrolls
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Dropdown } from 'react-bootstrap';
import { useInfiniteProducts } from '../../hooks/useProducts.js';
import { useMedRecFilter } from '../../context/MedRecFilterContext.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import ProductCard from '../../components/products/ProductCard.jsx';
import { DEFAULT_PRODUCT_SORT } from '../../constants/productSort.js';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get Med/Rec filter from global context
  const { filterValues } = useMedRecFilter();

  // Get URL parameters
  const searchQuery = searchParams.get('search') || '';
  const brandId = searchParams.get('brandId') || undefined;
  const initialSort = searchParams.get('sort') || DEFAULT_PRODUCT_SORT;
  const initialOrder = searchParams.get('order') || 'asc';
  const initialInStock = searchParams.get('inStock') === 'true';
  const initialOnSale = searchParams.get('sale') === 'true';
  const initialBackInStock = searchParams.get('backInStock') === 'true';

  // Local state
  const [sortBy, setSortBy] = useState(initialSort);
  const [sortDirection, setSortDirection] = useState(initialOrder);
  const [inStockOnly, setInStockOnly] = useState(initialInStock);
  const [onSaleOnly, setOnSaleOnly] = useState(initialOnSale);
  const [backInStockOnly, setBackInStockOnly] = useState(initialBackInStock);

  // Infinite scroll sentinel ref
  const sentinelRef = useRef(null);

  // Sync state with URL params when they change (e.g., navigating from /products?sale=true to /products?backInStock=true)
  useEffect(() => {
    setSortBy(initialSort);
    setSortDirection(initialOrder);
    setInStockOnly(initialInStock);
    setOnSaleOnly(initialOnSale);
    setBackInStockOnly(initialBackInStock);
  }, [initialSort, initialOrder, initialInStock, initialOnSale, initialBackInStock]);

  // Get products with infinite scroll
  const {
    products,
    pagination,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteProducts({
    pageSize: 24,
    sortBy,
    sortDirection,
    inStock: backInStockOnly ? true : inStockOnly,
    onSale: onSaleOnly,
    backInStock: backInStockOnly,
    search: searchQuery || undefined,
    brandId,
    isMed: filterValues.isMed,
    isRec: filterValues.isRec,
  });

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '600px',
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleObserver]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (brandId) params.set('brandId', brandId);
    if (sortBy !== DEFAULT_PRODUCT_SORT) params.set('sort', sortBy);
    if (sortDirection !== 'asc') params.set('order', sortDirection);
    if (inStockOnly) params.set('inStock', 'true');
    if (onSaleOnly) params.set('sale', 'true');
    if (backInStockOnly) params.set('backInStock', 'true');

    setSearchParams(params, { replace: true });
  }, [sortBy, sortDirection, inStockOnly, onSaleOnly, backInStockOnly, searchQuery, brandId, setSearchParams]);

  // Handle sort change
  const handleSortChange = (newSortBy, newDirection = 'asc') => {
    setSortBy(newSortBy);
    setSortDirection(newDirection);
  };

  // Clear search
  const handleClearSearch = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    setSearchParams(params);
  };

  // Page title based on context
  const getPageTitle = () => {
    if (searchQuery) {
      return `Search Results for "${searchQuery}"`;
    }
    if (onSaleOnly) {
      return 'Products On Sale';
    }
    if (backInStockOnly) {
      return 'Back In Stock';
    }
    return 'All Products';
  };

  return (
    <div className="bg-light min-vh-100">
      {/* Page Header */}
      <section className="bg-white py-4 border-bottom">
        <Container fluid className="">
          <Row className="align-items-center">
            <Col md={8}>
              {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/" className="text-decoration-none">Home</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {searchQuery ? 'Search Results' : 'Products'}
                  </li>
                </ol>
              </nav>

              {/* Page Title */}
              <div className="d-flex align-items-center mb-2">
                <div>
                  <h1 className="h2 mb-0">{getPageTitle()}</h1>
                  <p className="text-muted mb-0">
                    {pagination.totalItems || 0} products {searchQuery ? 'found' : 'available'}
                  </p>
                </div>
              </div>
            </Col>

            <Col md={4} className="text-md-end">
              {searchQuery && (
                <Button
                  variant="outline-secondary"
                  onClick={handleClearSearch}
                  className="me-2"
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear Search
                </Button>
              )}
              <Link to="/categories" className="btn btn-outline-primary">
                <i className="bi bi-grid me-1"></i>
                Browse Categories
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Filters and Sort Controls */}
      <section className="bg-white py-3 border-bottom">
        <Container fluid className="">
          <Row className="align-items-center justify-content-md-end">
            {!backInStockOnly && (
              <Col xs={6}>
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="inStockFilter"
                    label="In Stock Only"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    className="me-4"
                  />
                </div>
              </Col>
            )}
            <Col xs={6} className="d-flex align-items-center justify-content-md-end">
              <span className="me-3 text-muted">Sort by:</span>

              <Dropdown align="end">
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  {sortBy.includes('brandDisplayOrder') && 'Default'}
                  {sortBy === 'name' && 'Name'}
                  {sortBy === 'price' && 'Price'}
                  {sortBy === 'newest' && 'Newest'}
                  {sortBy === 'popular' && 'Popular'}
                  <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleSortChange(DEFAULT_PRODUCT_SORT, 'asc')}>
                    Default
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleSortChange('name', 'asc')}>
                    Name (A-Z)
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('name', 'desc')}>
                    Name (Z-A)
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleSortChange('price', 'asc')}>
                    Price (Low to High)
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('price', 'desc')}>
                    Price (High to Low)
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleSortChange('newest', 'desc')}>
                    Newest First
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Products Grid */}
      <section className="py-4">
        <Container fluid className="">
          {isLoading ? (
            <div className="text-center py-5">
              <LoadingSpinner />
              <p className="mt-3 text-muted">
                {searchQuery ? 'Searching products...' : 'Loading products...'}
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-5">
              <div className="alert alert-warning">
                <h5>Error Loading Products</h5>
                <p>{error.message}</p>
                <Button variant="primary" onClick={() => refetch()}>
                  Try Again
                </Button>
              </div>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="product-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="text-center py-4">
                {isFetchingNextPage && (
                  <>
                    <LoadingSpinner />
                    <p className="mt-2 text-muted">Loading more products...</p>
                  </>
                )}
                {!hasNextPage && products.length > 24 && (
                  <small className="text-muted">
                    Showing all {pagination.totalItems} products
                  </small>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-search display-1 text-muted mb-3"></i>
              <h3 className="text-muted">
                {searchQuery ? 'No Products Found' : 'No Products Available'}
              </h3>
              <p className="text-muted">
                {searchQuery
                  ? `No products match your search for "${searchQuery}".`
                  : 'There are no products available at this time.'
                }
              </p>
              <div className="d-flex justify-content-center gap-2">
                {searchQuery && (
                  <Button
                    variant="primary"
                    onClick={handleClearSearch}
                  >
                    Clear Search
                  </Button>
                )}
                <Link to="/categories" className="btn btn-outline-secondary">
                  Browse Categories
                </Link>
              </div>
            </div>
          )}
        </Container>
      </section>
    </div>
  );
};

export default ProductsPage;